<template>
  <div class="fines-table">
    <div class="d-flex search">
      <v-text-field
        v-model="searchString"
        class="fines-table__search"
        solo
        :placeholder="$t('fines.placeholder')"
      />
      <v-btn
        type="button"
        class="search-btn warning"
        depressed
        large
        @click="filters.search = searchString"
      >
        {{ $t('common.btns.search') }}
      </v-btn>
    </div>

    <v-flex d-flex>
      <v-menu
        ref="menuDate"
        :close-on-content-click="false"
        v-model="menuDate"
        :return-value.sync="filters.dates"
        max-width="280px"
        min-width="280px"
        class="pr-4 mt-2"
        transition="scale-transition"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-on="on"
            v-model="filters.dates"
            prepend-icon="mdi-calendar"
            :label="$t('common.fields.rentDate')"
            readonly
            class="fines-table__filter"
          />
        </template>
        <v-date-picker
          v-model="filters.dates"
          color="primary lighten-1"
          width="280"
          range
          :locale="$i18n.locale"
          :selected-items-text="filters.dates.toString()"
          :allowed-dates="allowedDates"
          @input="changeDate"
        ></v-date-picker>
      </v-menu>

      <v-select
        v-model="filters.status"
        class="fines-table__filter"
        item-text="name"
        item-value="id"
        :items="statuses"
        :label="$t('common.fields.status')"
        :loading="statusesLoading"
        clearable
      />
    </v-flex>

    <v-data-table
      class="mt-5"
      :headers="headers"
      :items="list"
      :loading="loading"
      :items-per-page="perPage"
      hide-default-footer
    >
      <template #item="{ item }">
        <tr :class="{ updating: itemsUpdating.includes(item.id) }">
          <td>{{ item.num }}</td>
          <td>{{ item.created_at }}</td>
          <td>{{ item.user ? item.user.name : '-' }}</td>
          <td>
            <template v-if="noEdit">
              {{ item.car.number }}
            </template>

            <auto-select
              v-else
              class="fines-table__auto"
              :number="item.car.number"
              :disabled="!$can('fines_edit_auto_driver')"
              hide-details
              @change="onItemUpdate(item.id, { car_id: $event.car_id })"
            />
          </td>
          <td>{{ item.car.investor.user.name }}</td>
          <td>
            <template v-if="noEdit">
              {{ item.violation_date }}
            </template>

            <violation-date-picker
              v-else
              :date-time="item.violation_date"
              :car-number="item.car.number"
              :item-id="item.id"
              @saved="onItemSaved"
            />
          </td>
          <td>
            <template v-if="noEdit">
              {{ item.driver.user.phone }}
            </template>

            <driver-select
              v-else
              permission="fines_edit_auto_driver"
              no-status
              hide-details
              :phone="item.driver.user.phone"
              :clearable="false"
              @change="onItemUpdate(item.id, { driver_id: $event.driver_id })"
            />
          </td>
          <td>{{ item.driver.user.name }}</td>
          <td>
            <template v-if="noEdit">
              {{ item.violation_number }}
            </template>

            <button
              v-else
              class="fines-table__edit-btn"
              :disabled="!$can('fines_edit_auto_driver')"
              @click="onEdit(item, 'violation_number')"
            >
              {{ item.violation_number }}
            </button>
          </td>
          <td :class="`status-${item.status}`">
            <template v-if="noEdit">
              {{ getStatusName(item.status) }}
            </template>

            <v-select
              v-else
              class="status-select"
              item-text="name"
              item-value="id"
              dense
              hide-details
              :items="statuses"
              :value="item.status"
              :item-disabled="({ id }) => id === 3"
              :disabled="!$can('fines_edit_status_amount_description')"
              @change="onItemUpdate(item.id, { status: $event })"
            />
          </td>
          <td class="no-wrap">
            <template v-if="noEdit">
              {{ formatAmount(item.amount) }}
            </template>

            <button
              v-else
              class="fines-table__edit-btn"
              :disabled="!$can('fines_edit_status_amount_description')"
              @click="onEdit(item, 'amount')"
            >
              {{ formatAmount(item.amount) }}
            </button>
          </td>
          <td>
            <template v-if="noEdit">
              {{ item.description || '-' }}
            </template>

            <button
              v-else
              class="fines-table__edit-btn"
              :disabled="!$can('fines_edit_status_amount_description')"
              @click="onEdit(item, 'description')"
            >
              {{ item.description || '-' }}
            </button>
          </td>
        </tr>
      </template>

      <template v-if="lastPage > 1" #footer>
        <v-divider />
        <div class="pt-1 pb-2">
          <v-pagination
            color="warning"
            :value="currentPage"
            :length="lastPage"
            dark
            circle
            @input="fetchList({ ...filters, page: $event })"
          />
        </div>
      </template>
    </v-data-table>

    <table-edit-dialog
      v-model="edit.dialog"
      :option="edit.option"
      :current-value="edit.value"
      :item-id="edit.itemId"
      @saved="onItemSaved"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import DriverSelect from '@/components/shared/DriverSelect.vue';
import AutoSelect from '@/components/shared/AutoSelect.vue';
import ViolationDatePicker from '@/components/fines/ViolationDatePicker.vue';
import TableEditDialog from '@/components/fines/TableEditDialog.vue';
import formatAmount from '@/helpers/formatAmount';

export default {
  props: {
    updated: Boolean,
    noEdit: Boolean,
    carId: [String, Number],
    driverId: [String, Number],
  },
  components: {
    DriverSelect,
    AutoSelect,
    ViolationDatePicker,
    TableEditDialog,
  },
  data: () => ({
    loading: false,
    menuDate: false,
    statusesLoading: false,
    itemsUpdating: [],
    searchString: '',
    filters: {
      search: '',
      dates: [],
      status: '',
    },
    edit: {
      dialog: false,
      option: '',
      itemId: '',
      value: null,
    },
  }),
  watch: {
    filters: {
      immediate: true,
      deep: true,
      handler(params) {
        if (params.dates.length === 1) return;
        this.fetchList(params);
      },
    },
    updated(value) {
      if (!value) return;
      this.updateList();
    },
  },
  computed: {
    ...mapState({
      list: ({ fines }) => fines.list,
      currentPage: ({ fines }) => fines.currentPage,
      lastPage: ({ fines }) => fines.lastPage,
      perPage: ({ fines }) => fines.perPage,
      statuses: ({ fines }) => fines.statuses,
    }),
    headers() {
      return [
        { text: this.$t('common.number'), sortable: false },
        { text: this.$t('common.table.created'), sortable: false },
        { text: this.$t('common.added'), sortable: false },
        { text: this.$t('auto.fields.number'), sortable: false },
        { text: this.$t('workersList.investorFullName'), sortable: false },
        { text: this.$t('fines.table.date'), sortable: false },
        { text: this.$t('common.fields.phone'), sortable: false },
        { text: this.$t('common.fields.fullName'), sortable: false },
        { text: this.$t('fines.table.prescriptionNumber'), sortable: false },
        { text: this.$t('common.fields.status'), sortable: false },
        { text: this.$t('common.fields.sum'), sortable: false },
        { text: this.$t('common.table.description'), sortable: false },
      ];
    },
  },
  methods: {
    formatAmount,
    changeDate(val) {
      if (val.length === 2) {
        this.$refs.menuDate.save(this.filters.dates);
      }
    },
    allowedDates(value) {
      const [from, to] = this.filters.dates;

      if (!from) return true;
      if (to) return true;

      const firstDate = new Date(from);
      const nextDate = new Date(value);

      return nextDate >= firstDate;
    },
    async fetchList(params) {
      this.loading = true;
      await this.$store.dispatch('fines/fetchList', {
        ...params,
        car_id: this.carId,
        driver_id: this.driverId,
      });
      this.loading = false;
    },
    async updateList() {
      await this.fetchList({ ...this.filters, page: this.currentPage });
      this.$emit('updated');
    },
    onEdit(item, option) {
      this.edit.dialog = true;
      this.edit.value = item[option];
      this.edit.itemId = item.id;
      this.edit.option = option;
    },
    async onItemUpdate(id, params) {
      this.itemsUpdating.push(id);

      const result = await this.$store.dispatch('fines/updateItem', {
        id,
        params,
      });

      this.onItemSaved(result);
      this.itemsUpdating = this.itemsUpdating.filter((item) => item !== id);
    },
    onItemSaved({ success }) {
      if (success) {
        this.updateList();
        this.edit.dialog = false;
      } else {
        this.$emit('update-error');
      }
    },
    getStatusName(id) {
      const status = this.statuses.find((item) => item.id === id);
      return status ? status.name : '-';
    },
  },
  async created() {
    if (!this.statuses.length) {
      this.statusesLoading = true;
      await this.$store.dispatch('fines/fetchStatuses');
      this.statusesLoading = false;
    }
  },
  beforeDestroy() {
    this.$store.commit('fines/setList', []);
  },
};
</script>

<style lang="scss">
.fines-table {
  width: 100%;
  line-height: 1.3;

  &__search {
    max-width: 400px;
  }

  &__filter {
    max-width: 230px;

    &:not(:last-child) {
      margin-right: 35px;
    }
  }

  &__auto {
    min-width: 85px;
  }

  &__edit-btn {
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      text-decoration: none;
    }

    &[disabled] {
      text-decoration: none;
      font-weight: normal;
    }
  }

  .status-select {
    width: 150px;
    .v-select__selection {
      font-size: 12px;
      margin-top: 2px;
      margin-bottom: 2px;
    }
  }

  .updating {
    pointer-events: none;
    opacity: 0.5;
  }

  .status-0 {
    background-color: #ffc4c4;
  }

  .status-1 {
    background-color: #fff79b;
  }

  .status-2,
  .status-3,
  .status-4,
  .status-5 {
    background-color: #c6f9c0;
  }

  .no-wrap {
    white-space: nowrap;
  }
}

.v-pagination__item,
.v-pagination__navigation {
  box-shadow: none;
}
</style>
